import React, { useState, useEffect } from 'react';
import { MODULES, ACTIONS } from '../context/PermissionsContext';
import api from '../services/api';

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [newRoleName, setNewRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const rolesData = await api.roles.getAll();
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);

    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await api.roles.getPermissions(roleId);
      setRolePermissions(response.permissions || {});
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setRolePermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    loadRolePermissions(role.id);
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    
    try {
      setLoading(true);
      await api.roles.create({ name: newRoleName, permissions: {} });
      setNewRoleName('');
      loadRoles();
      setMessage({ text: 'Role created successfully!', type: 'success' });
    } catch (error) {
      setMessage({ text: 'Error creating role', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (module, submodule, action) => {
    setRolePermissions(prev => {
      const modulePerms = prev[module] || {};
      const submodulePerms = modulePerms[submodule] || [];
      const hasPermission = submodulePerms.includes(action);
      
      return {
        ...prev,
        [module]: {
          ...modulePerms,
          [submodule]: hasPermission 
            ? submodulePerms.filter(a => a !== action)
            : [...submodulePerms, action]
        }
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      setLoading(true);
      await api.roles.updatePermissions(selectedRole.id, { permissions: rolePermissions });
      setMessage({ text: 'Role permissions updated successfully!', type: 'success' });
    } catch (error) {
      setMessage({ text: 'Error updating role permissions', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Role Management</h5>
      </div>
      <div className="card-body">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
            {message.text}
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label">Create New Role</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Role name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
              <button className="btn btn-success" onClick={handleCreateRole} disabled={loading}>
                Create
              </button>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Select Role ({roles.length} roles)</label>
            <select 
              className="form-control"
              onChange={(e) => {
                const role = roles.find(r => r.id === parseInt(e.target.value));
                if (role) handleRoleSelect(role);
              }}
              value={selectedRole?.id || ''}
            >
              <option value="">Choose a role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <button 
              className="btn btn-primary"
              onClick={handleSavePermissions}
              disabled={!selectedRole || loading}
            >
              {loading ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>

        {/* Existing Roles List */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Existing Roles</h6>
          </div>
          <div className="card-body">
            {roles.length === 0 ? (
              <p className="text-muted">No roles found. Create your first role above.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Role Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <tr key={role.id}>
                        <td>{role.id}</td>
                        <td>{role.name}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleRoleSelect(role)}
                          >
                            Edit Permissions
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {selectedRole && (
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Permissions for {selectedRole.name}</h6>
            </div>
            <div className="card-body">
              {Object.keys(MODULES).map(moduleKey => (
                <div key={moduleKey} className="mb-4">
                  <h6 className="text-primary">{moduleKey.replace('_', ' ')}</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Submodule</th>
                          {Object.values(ACTIONS).map(action => (
                            <th key={action} className="text-center">{action.toUpperCase()}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(MODULES[moduleKey].submodules).map(([subKey, subValue]) => (
                          <tr key={subKey}>
                            <td className="fw-bold">{subValue.replace(/_/g, ' ').toUpperCase()}</td>
                            {Object.values(ACTIONS).map(action => (
                              <td key={action} className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={rolePermissions[MODULES[moduleKey].name]?.[subValue]?.includes(action) || false}
                                  onChange={() => handlePermissionToggle(MODULES[moduleKey].name, subValue, action)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManager;